import React, { useCallback, useEffect, useState } from 'react';
import { Route, Switch, withRouter } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { startOfDay, subMinutes } from 'date-fns';

import { calendarUtils, selectors } from '@fernleaf/util';
import { 
    setCalendar, getCalendar, getSystemControls, setSystemControls,
    setMenusAndModifiers, getMenus,
    setItemOrderControls,
    getCustomer, setCustomer, getCustomers, setCustomers,
    getPickupDate, setPickupDate,
    clearCart, setOrderToPay, getOrderToPay, getReturnToAdmin, getRestart,
    getShowOrders, setShowOrders 
} from './store/redux';

import apiGetCustomer from './api/getCustomer';
import apiGetOrder from './api/getOrder';
import apiGetAvailableMenus from './api/getAvailableMenus';
import apiGetItemOrderControls from './api/getItemOrderControls';
import apiGetCalendar from './api/getCalendar';
import apiGetSystemControls from './api/getSystemControls';
import apiCodeVersion from './api/codeVersion';

import classes from './App.module.css';

import Layout from './Layout/Layout';
import { LoginRouter, Theme, Modal, Button } from '@fernleaf/util';

import Main from './Main/Main';
import Cart from './Cart/Cart';
import Checkout from './Checkout/Checkout';
import Orders from './Orders/Orders';
import Products from './Products/Products';
import SavedCarts from './SavedCarts/SavedCarts';
import apiGetCustomers from './api/customer/apiGetCustomers';

const MAIN_PAGE = /shop\/?$/;

const App = ( { location, history } ) => {  
    const dispatch = useDispatch();
    const user = useSelector( selectors.getUser );
    const systemControls = useSelector( getSystemControls ); 
    const calendar = useSelector( getCalendar );
    const menus = useSelector( getMenus );
    const customer = useSelector( getCustomer ); // If staff is ordering for a customer then the user
                                                 // is the admin user for the staff person and the
                                                 // customer is an additional user stored as 'customer'.
    const customers = useSelector( getCustomers );
    const orderToPay = useSelector( getOrderToPay );
    const returnToAdmin = useSelector( getReturnToAdmin );
    const restart = useSelector( getRestart );
    const pickupDate = useSelector( getPickupDate );
    const showOrders = useSelector( getShowOrders );
    const [ currentCodeVersion, setCurrentCodeVersion ] = useState( null );
    const [ outOfDate, setOutOfDate ] = useState( null );
    const [ bannerDisplayed, setBannerDisplayed ] = useState( false );

    const checkCodeVersion = useCallback( () => {
        apiCodeVersion( ( { codeVersion } ) => {
            if ( currentCodeVersion === null ) {
                setCurrentCodeVersion( codeVersion );
            } else if ( codeVersion !== currentCodeVersion ) {
                setOutOfDate( true );
            }
        }, dispatch );
    }, [ currentCodeVersion, dispatch ] );

    const refreshExternals = useCallback( () => {
        checkCodeVersion();
        apiGetItemOrderControls(
            ( controls ) => dispatch( setItemOrderControls( controls ) ),
            dispatch
        )
    }, [ checkCodeVersion, dispatch ] );

    const displayBanner = useCallback( () => 
        location.pathname.match( MAIN_PAGE ) &&
        calendar &&
        ! user.isAdmin &&
        systemControls && systemControls.bannerActive && 
        ! bannerDisplayed
    , [ bannerDisplayed, calendar, user, systemControls, location ] );

    const acknowledgeBanner = useCallback( (e) => {
        e.stopPropagation(); 
        setBannerDisplayed( true );
    }, [ setBannerDisplayed ] );

    useEffect( () => {
        // When coming from the admin console the search parms should identify the customer
        // for the order, otherwise ignore any search parms.
        // This should only be done on first entry to the shop app.
        if ( location.search.startsWith( '?' ) ) {
            if ( location.search.startsWith( '?customer=' ) ) {
                localStorage.setItem( 'adminParms', location.search );
            } else {
                localStorage.removeItem( 'adminParms' );
            }
            if ( location.pathname !== "/shop/loginerror" && location.pathname !== "/shop/emailreset" ) {
                dispatch( clearCart() );
                dispatch( setPickupDate( null ) );
                history.replace( location.pathname );
            }
        }
    // eslint-disable-next-line
    }, [] )

    useEffect( () => {
        if ( returnToAdmin ) {
            let returnPath = localStorage.returnPath;
            if ( ! returnPath || ! returnPath.startsWith( '/admin' ) ) { // Make sure we have a valid return path
                returnPath = '/admin';
            };
            localStorage.removeItem( 'returnPath' );
            setTimeout ( () => window.location = `${ window.adminRoot() }${ returnPath }`, 50 );
        }
    }, [ returnToAdmin ] );

    useEffect( () => {
        if ( restart ) {
            setTimeout( () => window.location = `${ window.location.origin }/shop/products`, 50 );
        }
    }, [ restart ] );

    useEffect( () => {
        refreshExternals();
        const interval = setInterval( refreshExternals, 60000 );
        return () => clearInterval( interval );
    }, [ refreshExternals ] );

    useEffect( () => {
        if ( ! systemControls ) {
            apiGetSystemControls( 
                ( controls ) => dispatch( setSystemControls( controls ) ),
                dispatch 
            );
        }
    }, [ systemControls, dispatch ] );

    useEffect( () => {
        if ( systemControls && systemControls.inPerson && customers.length === 0 ) {
            apiGetCustomers( 
                ( customers ) => dispatch( setCustomers( customers ) ), 
                dispatch 
            ); 
        }
    }, [ customers, systemControls, dispatch ] );

    useEffect( () => {
        // Load calendar and menus to redux - used elsewhere
        if ( ! calendar ) {
            apiGetCalendar(
                calendar => dispatch( setCalendar( calendar ) ),
                dispatch
            )
        }
        if ( ! menus ) {
            apiGetAvailableMenus( 
                menusAndModifiers => dispatch( setMenusAndModifiers( menusAndModifiers ) ),
                dispatch
            );
        }
    }, [ calendar, menus, dispatch ] );

    useEffect( () => {
        const checkPickupTimeout = () => {
            if ( pickupDate && systemControls && ! systemControls.storeOrder ) {
                // Have we passed the previously requested pickup date & time? If so reset.
                const now = new Date();
                if ( pickupDate !== startOfDay( now ) && pickupDate < subMinutes( now, 5 ) ) {
                    dispatch( setPickupDate( user.isAdmin ? now : null ) );
                }
            }
        };

        const interval = setInterval( checkPickupTimeout, 60000 );
        return () => clearInterval( interval );
    }, [ pickupDate, user, systemControls, dispatch ] );

    useEffect( () => {

        // Store URL parameters in local storage in case admin user reloads the page
        // by accident. Then reload the page with no parms.

        if ( location.search.startsWith( '?' ) ) {
            // The initial useEffect function should clear this out first.
            return;
        }

        if ( ! user.id ) { // If not logged in then ignore any URL parameters
            return;
        }
        
        // Manage values related to whether we are ordering on behalf of a customer
        if ( ! user.isAdmin ) {
            localStorage.removeItem( 'adminParms' );
            if ( customer ) {
                dispatch( setCustomer( null ) );
            }
            return;
        }

        if ( ! localStorage.adminParms ) {
            // Probably logged in at the shop page - this does not work
            setTimeout ( () => window.location = `${ window.adminRoot() }/admin`, 50 );
            return;
        }

        if ( ! calendar ) {
            return; // Wait till calendar is loaded
        }

        if ( ! systemControls || systemControls.inPerson !== undefined ) { // Wait for system controls, only init once
            return;
        }

        const urlParms = new URLSearchParams( localStorage.adminParms );

        const returnPath = urlParms.get( 'return' );
        if ( returnPath ) {
            localStorage.setItem( 'returnPath', returnPath );
        } else {
            localStorage.removeItem( 'returnPath' );
        }

        const customerId = urlParms.get( 'customer' );
        const payOrderId = urlParms.get( 'pay' );

        const orderType  = urlParms.get( 'type' );
        const inPerson   = orderType === 'in_person';
        const storeOrder = orderType === 'store_order'
        
        systemControls.inPerson   = inPerson;
        systemControls.storeOrder = storeOrder;
        dispatch( setSystemControls( { ...systemControls } ) );

        if ( ! payOrderId && ! pickupDate ) { // Initialize pickup date unless it is a page reload
            if ( systemControls.storeOrder || systemControls.inPerson ) {
                dispatch( setPickupDate(
                    systemControls.storeOrder ? 
                        calendarUtils.hourBeforeNextOpening( calendar ) :
                        new Date()
                ) );
            }
        }
        
        if ( payOrderId && ( ! orderToPay || orderToPay._id !== payOrderId ) ) { 
            const reloadOrder = orders => {
                orders.order.squareOrder = orders.squareOrder;
                dispatch( setOrderToPay( orders.order ) );
            }
            apiGetOrder( payOrderId, reloadOrder, dispatch )
        }

        if ( inPerson ) {
            if ( ! customer ) {
                dispatch( setCustomer( // Create dummy customer
                    {
                        name:  '',
                        email: '',
                        phone: ''
                    }
                ) );
            }

        } else if ( ! customer || customer.id !== customerId ) {

            apiGetCustomer( 
                customerId, 
                ( customer ) => {
                    dispatch( setCustomer( customer ) );

                    if ( ! pickupDate && ! payOrderId && ! inPerson && ! storeOrder ) { 
                        // If ordering for a customer prompt for pickup date up front
                        setTimeout( () => document.getElementById( 'fernPickupDateTime' ).click(), 100 );
                    }
                },
                () => {} // Swallow errors - probably navigating back to admin 
            );
        }

    }, [ user, customer, orderToPay, pickupDate, calendar, systemControls, location, history, dispatch ] );
 
    if ( returnToAdmin ) {
        localStorage.removeItem( 'adminParms' );
        return (
            <div className={ classes.App } style={{ backgroundImage: 'url(/pictures/other/Background.png)' }} >
                &nbsp;
            </div>
        );
    }

    return ( 
        <div className={ classes.App } style={{ backgroundImage: 'url(/pictures/other/Background.png)' }} >
            <audio id='fernleaf-orders-chime'>
                <source src='/fernleaf.chime.mp3' type='audio/mpeg' />
            </audio>
            <Theme useDarkTheme={ true } />
            <LoginRouter app='shop' >
                <Layout>
                    <Switch>
                        <Route exact path='/products' component={ Products } />
                        <Route exact path='/cart' component={ Cart } />
                        <Route exact path='/checkout' component={ Checkout } />
                        <Route exact path='/orders' component={ Orders } />
                        <Route exact path='/saved' component={ SavedCarts } />
                        <Route path='/' component={ Main } />
                    </Switch>
                </Layout>
            </LoginRouter>

            { outOfDate && 
                <Modal noX onClick={ () => window.location = '/shop' } >
                    The page you are using is a little out of date.
                    <Button>Refresh Page</Button>
                </Modal>
            }

            { displayBanner() &&
                <Modal noX onClick={ acknowledgeBanner } >
                    <div className={ classes.Banner }>{ systemControls.banner }</div>
                    <div className={ classes.BannerButton } >
                        <Button>OK</Button>
                    </div>
                </Modal>
            }

            { showOrders &&
                <Modal className={ classes.OrderDetails } contentClassName={ classes.OrderDetailsContent }
                    backdropClassName={ classes.OrderDetailsBackdrop }
                    onClick={ () => dispatch( setShowOrders( false ) ) }
                >
                    <iframe title='ORDER-DETAILS'
                        src={ `${ window.adminRoot() }/admin/order-details` } 
                            className={ classes.OrderDetailsFrame }
                    />
                </Modal>
            }
        </div>
    );
};

export default withRouter( App );
